<template>
    <div class="whatsapp-comparison">
        <div class="comparison-header">
            <h2 class="title">{{ $t("message.whatsappSolutionsComparison") }}</h2>
            <div class="search-container">
                <a-input-search
                    v-model="searchQuery"
                    :placeholder="$t('message.searchFeaturePlaceholder')"
                    style="max-width: 300px"
                    @input="filterFeatures"
                />
            </div>
        </div>

        <div class="table-wrapper">
            <div class="table-header-fixed">
                <table class="comparison-table">
                    <thead>
                        <tr>
                            <th class="feature-column">{{ $t("message.feature") }}</th>
                            <th class="solution-column">
                                <div class="solution-header">
                                    <div class="img whatsapp">
                                        <WhatsappV3Icon style="width: 24px; height: 24px" />
                                    </div>
                                    <div class="solution-info">
                                        <span>{{ $t("message.whatsappV3") }}</span>
                                        <span class="solution-subtitle">{{ $t("message.qrCodeUnofficialSolution") }}</span>
                                    </div>
                                </div>
                            </th>
                            <th class="solution-column">
                                <div class="solution-header">
                                    <div class="img whatsapp">
                                        <WhatsappBusinessIcon style="width: 24px; height: 24px" />
                                    </div>
                                    <div class="solution-info">
                                        <span>{{ $t("message.whatsappWaba") }}</span>
                                        <span class="solution-subtitle">{{ $t("message.metaOfficialApiSolution") }}</span>
                                    </div>
                                </div>
                            </th>
                            <th class="solution-column">
                                <div class="solution-header">
                                    <div class="img whatsapp-coex">
                                        <WhatsappBusinessIcon style="width: 24px; height: 24px" />
                                    </div>
                                    <div class="solution-info">
                                        <span>{{ $t("message.wabaCoex") }}</span>
                                        <span class="solution-subtitle">{{ $t("message.supsisProviderCoexistence") }}</span>
                                    </div>
                                </div>
                            </th>
                        </tr>
                    </thead>
                </table>
            </div>
            <div class="table-body-scrollable">
                <table class="comparison-table">
                    <colgroup>
                        <col class="feature-column-width">
                        <col class="solution-column-width">
                        <col class="solution-column-width">
                        <col class="solution-column-width">
                    </colgroup>
                    <tbody>
                        <template v-for="category in filteredCategories">
                            <tr :key="'header-' + category.name" class="category-header">
                                <td colspan="4">
                                    <div class="category-title">
                                        <span class="material-icons">{{ category.icon }}</span>
                                        {{ category.name }}
                                    </div>
                                </td>
                            </tr>
                            <tr
                                v-for="feature in category.features"
                                :key="category.name + '-' + feature.name"
                                class="feature-row"
                            >
                                <td class="feature-name">
                                    <span>{{ feature.name }}</span>
                                    <a-tooltip v-if="feature.description" :title="feature.description">
                                        <span class="material-icons info-icon">info</span>
                                    </a-tooltip>
                                </td>
                                <td class="support-cell">
                                    <div class="support-status" :class="getSupportClass(feature.v3)">
                                        <span class="material-icons support-icon">{{ getSupportIcon(feature.v3) }}</span>
                                    </div>
                                    <div v-if="feature.v3_note" class="support-note">{{ feature.v3_note }}</div>
                                </td>
                                <td class="support-cell">
                                    <div class="support-status" :class="getSupportClass(feature.waba)">
                                        <span class="material-icons support-icon">{{ getSupportIcon(feature.waba) }}</span>
                                    </div>
                                    <div v-if="feature.waba_note" class="support-note">{{ feature.waba_note }}</div>
                                </td>
                                <td class="support-cell">
                                    <div class="support-status" :class="getSupportClass(feature.coex)">
                                        <span class="material-icons support-icon">{{ getSupportIcon(feature.coex) }}</span>
                                    </div>
                                    <div v-if="feature.coex_note" class="support-note">{{ feature.coex_note }}</div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="comparison-legend">
            <div class="legend-item">
                <span class="material-icons supported">check_circle</span>
                <span>Destekleniyor</span>
            </div>
            <div class="legend-item">
                <span class="material-icons partial">help</span>
                <span>Kısmi Destek</span>
            </div>
            <div class="legend-item">
                <span class="material-icons not-supported">cancel</span>
                <span>Desteklenmiyor</span>
            </div>
        </div>
    </div>
</template>

<script>
import WhatsappV3Icon from "../views/Pages/Chatbot/pages/integrations/svg/specialWhatsappIcon/WhatsappV3.vue";
import WhatsappBusinessIcon from "../views/Pages/Chatbot/pages/integrations/svg/WhatsappBusinessIcon.vue";

export default {
    name: "WhatsappComparison",
    components: {
        WhatsappV3Icon,
        WhatsappBusinessIcon,
    },
    data() {
        return {
            searchQuery: "",
            categories: [
                {
                    name: "Temel Mesajlaşma",
                    icon: "message",
                    features: [
                        {
                            name: "Metin Mesajı Alma",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Metin Mesajı Gönderme",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Resim Gönderme",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Resim Alma",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Dosya Gönderme",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Dosya Alma",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Video Gönderme",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Video Alma",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Ses Mesajı Gönderme",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Ses Mesajı Alma",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                    ],
                },
                {
                    name: "Özel Mesaj Türleri",
                    icon: "star",
                    features: [
                        {
                            name: "Profil Video Notu Gönderme",
                            v3: "not_supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Profil Video Notu Alma",
                            v3: "partial_supported",
                            v3_note: "JSON mesajı olarak gözükür, LLM'ler anlayabilir",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Anket & Oylama Gönderme",
                            v3: "not_supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Anket & Oylama Alma",
                            v3: "partial_supported",
                            v3_note: "JSON mesajı olarak gözükür, LLM'ler anlayabilir",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Oylama Cevabı Alma",
                            v3: "partial_supported",
                            v3_note: "JSON mesajı olarak gözükür, LLM'ler anlayabilir",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Etkinlik Mesajı Gönderme",
                            v3: "not_supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Etkinlik Mesajı Alma",
                            v3: "partial_supported",
                            v3_note: "JSON mesajı olarak gözükür, LLM'ler anlayabilir",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Etkinlik Katılım Cevapları",
                            v3: "partial_supported",
                            v3_note: "JSON mesajı olarak gözükür, LLM'ler anlayabilir",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Ürün Kataloğu Sepet Onayı",
                            v3: "partial_supported",
                            v3_note: "JSON mesajı olarak gözükür, LLM'ler anlayabilir",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                    ],
                },
                {
                    name: "Konum & İletişim",
                    icon: "location_on",
                    features: [
                        {
                            name: "Konum Mesajı Gönderme",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Konum Mesajı Alma",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Canlı Konum Gönderme",
                            v3: "not_supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Canlı Konum Alma",
                            v3: "not_supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "VCard Gönderme",
                            v3: "not_supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "VCard Alma",
                            v3: "supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Çoklu VCard Gönderme",
                            v3: "not_supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Çoklu VCard Alma",
                            v3: "supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                    ],
                },
                {
                    name: "Medya & Eğlence",
                    icon: "photo_library",
                    features: [
                        {
                            name: "Sticker Gönderme",
                            v3: "not_supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Sticker Alma",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "GIF Gönderme",
                            v3: "supported",
                            v3_note: "Karşı tarafa video gibi gözükebilir",
                            waba: "supported",
                            waba_note: "Karşı tarafa video gibi gözükebilir",
                            coex: "supported",
                            coex_note: "Karşı tarafa video gibi gözükebilir",
                        },
                        {
                            name: "GIF Alma",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                    ],
                },
                {
                    name: "Grup & Toplu Mesajlaşma",
                    icon: "group",
                    features: [
                        {
                            name: "Toplu Mesaj Gönderimi (Broadcast)",
                            v3: "not_supported",
                            v3_note: "Banlanma riski var",
                            waba: "supported",
                            coex: "supported",
                            coex_note: "API üzerinden desteklenir, ancak uygulama yayın listeleri CoEx'te devre dışıdır",
                        },
                        {
                            name: "Uygulama Yayın Listeleri",
                            description: "WhatsApp Business uygulaması üzerinden yayın listesi kullanımı",
                            v3: "not_supported",
                            waba: "not_supported",
                            waba_note: "Uygulama erişimi yok",
                            coex: "not_supported",
                            coex_note: "Meta tarafından CoEx modunda uygulama yayın listeleri devre dışı bırakılır",
                        },
                        {
                            name: "Grup Mesajları Alma",
                            v3: "supported",
                            waba: "not_supported",
                            waba_note: "WABA'lar gruplara eklenemez",
                            coex: "partial_supported",
                            coex_note: "Grup mesajları sadece uygulamada görülebilir, API'ye sync olmaz",
                        },
                        {
                            name: "Gruplara Mesaj Gönderme",
                            v3: "supported",
                            v3_note: "Banlanma riski var",
                            waba: "not_supported",
                            coex: "partial_supported",
                            coex_note: "Sadece uygulama üzerinden, API'de grup desteği yoktur",
                        },
                        {
                            name: "Newsletter Kanalları",
                            v3: "supported",
                            waba: "not_supported",
                            waba_note: "WABA'lar newsletter kanallarına abone olamaz",
                            coex: "not_supported",
                            coex_note: "WABA'lar newsletter kanallarına abone olamaz",
                        },
                    ],
                },
                {
                    name: "Reklam & Pazarlama",
                    icon: "campaign",
                    features: [
                        {
                            name: "CTWA Reklam Etkileşimi (Temel)",
                            v3: "supported",
                            waba: "supported",
                            waba_note: "Full detaylı bilgi (reklam, reklam seti, kampanya)",
                            coex: "supported",
                            coex_note: "Full detaylı bilgi + 72 saat ücretsiz sohbet penceresi",
                        },
                        {
                            name: "CTWA Reklam Etkileşimi (Detaylı)",
                            v3: "not_supported",
                            waba: "supported",
                            waba_note: "Full detaylı bilgi (reklam, reklam seti, kampanya)",
                            coex: "supported",
                            coex_note: "Full detaylı bilgi (reklam, reklam seti, kampanya)",
                        },
                        {
                            name: "CTWA 72 Saat Ücretsiz Pencere",
                            description: "Click-to-WhatsApp reklamlarından gelen sohbetlerde 72 saatlik ücretsiz mesajlaşma",
                            v3: "not_supported",
                            waba: "supported",
                            waba_note: "CTWA reklamlarından gelen sohbetlerde 72 saat ücretsiz mesajlaşma",
                            coex: "supported",
                            coex_note: "CTWA reklamlarından gelen sohbetlerde 72 saat ücretsiz mesajlaşma",
                        },
                    ],
                },
                {
                    name: "Kullanıcı Deneyimi",
                    icon: "person",
                    features: [
                        {
                            name: "Yazıyor İndikatörü Gönderme",
                            v3: "not_supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Birden Çok Cihaz Desteği",
                            v3: "supported",
                            waba: "not_supported",
                            waba_note: "WABA'lar sadece yazılımsal olarak yönetilir",
                            coex: "partial_supported",
                            coex_note: "CoEx açıldığında bağlı cihazlar çıkarılır. Sonrasında desteklenen cihazlar tekrar bağlanabilir (WhatsApp Web, Mac, vb.)",
                        },
                        {
                            name: "Otomasyondan Mesaj Gönderme",
                            v3: "partial_supported",
                            v3_note: "Banlanma riski var",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "REST API ile Mesaj Gönderme",
                            v3: "not_supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Profil Bilgilerini Düzenleme",
                            v3: "not_supported",
                            waba: "supported",
                            coex: "supported",
                            coex_note: "Hem uygulamadan hem API'den düzenlenebilir",
                        },
                        {
                            name: "Mesaj Gönderenin Adı",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Profil Fotoğrafını Alma",
                            v3: "supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Chat Başlatma",
                            v3: "supported",
                            v3_note: "Herhangi bir numaraya direkt chat başlatma yapılabilir",
                            waba: "partial_supported",
                            waba_note: "Şablon mesajlar ile chat başlatılabilir (ücretli)",
                            coex: "supported",
                            coex_note: "API'den şablon ile veya uygulamadan direkt serbest metin ile başlatılabilir",
                        },
                        {
                            name: "Mesaj Ücreti (API)",
                            description: "Cloud API üzerinden gönderilen mesajların ücretlendirilmesi",
                            v3: "not_supported",
                            v3_note: "Gelen giden mesajlardan herhangi bir ücret alınmaz",
                            waba: "partial_supported",
                            waba_note: "Tüm iş başlatıcı mesajlar ücretli, müşteri başlatıcı (servis) mesajlar ücretsiz. wabapricing.supsis.ai",
                            coex: "partial_supported",
                            coex_note: "API mesajları ücretli, uygulama mesajları tamamen ücretsiz. Servis sohbetleri (müşteri başlatıcı) ücretsiz",
                        },
                        {
                            name: "Uygulama Mesajları Ücretsiz",
                            description: "WhatsApp Business uygulaması üzerinden gönderilen mesajların ücretsiz olması",
                            v3: "supported",
                            v3_note: "Tüm mesajlar ücretsiz",
                            waba: "not_supported",
                            waba_note: "Uygulama erişimi yok, tüm mesajlar API'den gider",
                            coex: "supported",
                            coex_note: "Uygulama üzerinden gönderilen mesajlar tamamen ücretsiz kalır (Meta resmi politikası)",
                        },
                        {
                            name: "Telefondan WhatsApp Kullanımı",
                            v3: "supported",
                            v3_note: "Cihazda WhatsApp aktif olmalıdır",
                            waba: "not_supported",
                            waba_note: "Numara tamamen API'ye atanır, telefonda kullanılamaz",
                            coex: "supported",
                            coex_note: "API kullanırken mobil WhatsApp Business uygulaması aynı anda sorunsuz kullanılabilir",
                        },
                        {
                            name: "Sohbet Geçmişi Korunması",
                            description: "API'ye geçiş sırasında mevcut sohbet geçmişinin korunması",
                            v3: "not_supported",
                            v3_note: "API'ye geçişte sohbet geçmişi kaybolur",
                            waba: "not_supported",
                            waba_note: "Numara API'ye kaydedildiğinde sohbet geçmişi kaybolur",
                            coex: "supported",
                            coex_note: "CoEx, mevcut numarayı uygulama ile birlikte onboard eder, sohbet geçmişi ve kişiler korunur",
                        },
                        {
                            name: "Mesaj Yansıması (Echo)",
                            description: "API'den gönderilen mesajların uygulamada, uygulamadan gönderilenlerin API'de görünmesi",
                            v3: "not_supported",
                            waba: "not_supported",
                            waba_note: "Uygulama erişimi yok",
                            coex: "supported",
                            coex_note: "smb_message_echoes webhook'u ile mesajlar her iki tarafta da senkronize olur",
                        },
                        {
                            name: "Kaybolan Mesajlar (Disappearing)",
                            description: "WhatsApp'ın kaybolan mesajlar özelliği",
                            v3: "supported",
                            waba: "not_supported",
                            coex: "not_supported",
                            coex_note: "Meta tarafından CoEx modunda kaybolan mesajlar devre dışı bırakılır",
                        },
                        {
                            name: "Durum/Hikayeler (Status)",
                            description: "WhatsApp Status/Stories özelliği",
                            v3: "not_supported",
                            v3_note: "Supsis'te desteklenmez",
                            waba: "not_supported",
                            coex: "partial_supported",
                            coex_note: "Uygulamada kullanılabilir, ancak API'ye sync olmaz",
                        },
                    ],
                },
                {
                    name: "Güvenilirlik & Performans",
                    icon: "security",
                    features: [
                        {
                            name: "Stabilite & Yüksek Erişilebilirlik",
                            description: "Sistem kararlılığı ve sürekli erişilebilirlik",
                            v3: "not_supported",
                            v3_note:
                                "Sürekli QR okutma gerekebilir, bağlantılar stabil değil, mesaj senkronizasyonu bozulabilir, hesap banlanabilir. Düşük SLA için uygun.",
                            waba: "supported",
                            waba_note:
                                "Resmi çözüm, %99.9 SLA, Meta kuralları çerçevesinde banlanma riski yok, bağlantı kopma sorunu olmaz.",
                            coex: "supported",
                            coex_note:
                                "Cloud API tarafı resmi çözüm ile %99.9 SLA sağlar. Uygulama tarafı da paralel çalışmaya devam eder.",
                        },
                        {
                            name: "Ban Riski",
                            v3: "partial_supported",
                            v3_note: "Unofficial çözüm olduğu için ban riski mevcuttur",
                            waba: "supported",
                            waba_note: "Meta kurallarına uyulduğu sürece ban riski yoktur",
                            coex: "supported",
                            coex_note: "Meta'nın resmi çözümüdür, kurallarına uyulduğu sürece ban riski yoktur",
                        },
                        {
                            name: "Unofficial API Uyumluluğu",
                            description: "Unofficial WhatsApp API'leri ile birlikte kullanılabilirlik",
                            v3: "supported",
                            v3_note: "V3 zaten unofficial bir çözümdür",
                            waba: "not_supported",
                            waba_note: "Sadece resmi Cloud API",
                            coex: "not_supported",
                            coex_note: "CoEx etkinleştirildiğinde unofficial API'ler devre dışı bırakılır (Meta politikası)",
                        },
                    ],
                },
                {
                    name: "Mesaj Yönetimi",
                    icon: "edit",
                    features: [
                        {
                            name: "Mesaj Reaksiyonu Gönderme",
                            v3: "not_supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Mesaj Reaksiyonu Alma",
                            v3: "supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Mesaj Silme (Kendi Tarafınız)",
                            v3: "not_supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Mesaj Silme Bilgisi (Müşteri)",
                            v3: "supported",
                            v3_note: "Supsis mesajı silmez fakat silindiği bilgisini gösterir",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Mesaj Düzenleme (Kendi Tarafınız)",
                            v3: "not_supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Mesaj Düzenleme Bilgisi (Müşteri)",
                            v3: "supported",
                            v3_note: "Düzenlendiği bilgisi Supsis'te görülür",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Mesaj Caption Desteği",
                            v3: "partial_supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                        {
                            name: "Mesaj Alıntılama (Gönderme)",
                            v3: "supported",
                            waba: "partial_supported",
                            coex: "partial_supported",
                        },
                        {
                            name: "Mesaj Alıntılama (Alma)",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Tek Görünümlük Mesaj Alma",
                            v3: "supported",
                            waba: "supported",
                            coex: "supported",
                        },
                        {
                            name: "Tek Görünümlük Mesaj Gönderme",
                            v3: "not_supported",
                            waba: "not_supported",
                            coex: "not_supported",
                        },
                    ],
                },
                {
                    name: "Arama & Sesli İletişim",
                    icon: "phone",
                    features: [
                        {
                            name: "WhatsApp Arama Desteği",
                            v3: "not_supported",
                            v3_note: "Supsis'te cevaplayamazsınız, diğer cihazlarınızdan cevaplayabilirsiniz",
                            waba: "partial_supported",
                            waba_note: "WABA voice to PBX açarsanız Supsis'te çağrıları alabilirsiniz",
                            coex: "partial_supported",
                            coex_note: "WABA voice to PBX açarsanız Supsis'te çağrıları alabilirsiniz",
                        },
                        {
                            name: "Arama Loglarının Alınması",
                            v3: "not_supported",
                            waba: "partial_supported",
                            waba_note: "WABA voice to PBX açarsanız Supsis'te çağrıları alabilirsiniz",
                            coex: "partial_supported",
                            coex_note: "WABA voice to PBX açarsanız Supsis'te çağrıları alabilirsiniz",
                        },
                        {
                            name: "Call to PBX Yönlendirme",
                            v3: "not_supported",
                            waba: "supported",
                            waba_note: "NetGSM, Twilio, Telnyx gibi sistemlere çağrı aktarılabilir",
                            coex: "supported",
                            coex_note: "NetGSM, Twilio, Telnyx gibi sistemlere çağrı aktarılabilir",
                        },
                        {
                            name: "Supsis'ten WhatsApp Arama",
                            v3: "not_supported",
                            waba: "not_supported",
                            waba_note: "Beta olarak önümüzdeki aylarda gelecek",
                            coex: "not_supported",
                            coex_note: "Beta olarak önümüzdeki aylarda gelecek",
                        },
                    ],
                },
                {
                    name: "Senkronizasyon & CoEx Özel",
                    icon: "sync",
                    features: [
                        {
                            name: "Diğer Cihazdan Gönderilen Mesajlar",
                            v3: "supported",
                            v3_note: "Tüm mesajlar sync olur Supsis'te",
                            waba: "not_supported",
                            waba_note: "WABA'lar zaten cihazda çalışan bir versiyon değildir",
                            coex: "supported",
                            coex_note: "Uygulamadan gönderilen mesajlar smb_message_echoes webhook'u ile API'ye yansır",
                        },
                        {
                            name: "API Mesajlarının Uygulamada Görünmesi",
                            description: "Cloud API üzerinden gönderilen mesajların WhatsApp Business uygulamasında görünmesi",
                            v3: "not_supported",
                            waba: "not_supported",
                            waba_note: "Uygulama erişimi yok",
                            coex: "supported",
                            coex_note: "API'den gönderilen mesajlar otomatik olarak uygulamada da görünür",
                        },
                        {
                            name: "Bağımsız Oturum Yönetimi",
                            description: "Uygulama ve API oturumlarının birbirinden bağımsız çalışması",
                            v3: "not_supported",
                            waba: "not_supported",
                            waba_note: "Sadece API oturumu",
                            coex: "supported",
                            coex_note: "Uygulama ve Cloud API oturumları paralel ve bağımsız çalışır, birbirlerini etkilemez",
                        },
                        {
                            name: "13 Gün Aktiflik Zorunluluğu",
                            description: "Uygulamanın belirli süre içinde açılma zorunluluğu",
                            v3: "not_supported",
                            v3_note: "Böyle bir zorunluluk yok, sürekli bağlı",
                            waba: "not_supported",
                            waba_note: "Böyle bir zorunluluk yok",
                            coex: "partial_supported",
                            coex_note: "WhatsApp Business uygulaması en az 13 günde bir açılmalıdır, aksi halde hesap deaktif olabilir",
                        },
                    ],
                },
            ],
            filteredCategories: [],
        };
    },
    mounted() {
        this.filteredCategories = this.categories;
    },
    methods: {
        getSupportClass(status) {
            return {
                supported: status === "supported",
                partial: status === "partial_supported",
                "not-supported": status === "not_supported",
            };
        },
        getSupportIcon(status) {
            switch (status) {
                case "supported":
                    return "check_circle";
                case "partial_supported":
                    return "help";
                case "not_supported":
                    return "cancel";
                default:
                    return "help";
            }
        },
        getSupportText(status) {
            switch (status) {
                case "supported":
                    return "Destekleniyor";
                case "partial_supported":
                    return "Kısmi Destek";
                case "not_supported":
                    return "Desteklenmiyor";
                default:
                    return "Bilinmiyor";
            }
        },
        filterFeatures() {
            if (!this.searchQuery.trim()) {
                this.filteredCategories = this.categories;
                return;
            }

            const query = this.searchQuery.toLowerCase();
            this.filteredCategories = this.categories
                .map((category) => {
                    const filteredFeatures = category.features.filter(
                        (feature) =>
                            feature.name.toLowerCase().includes(query) ||
                            (feature.description && feature.description.toLowerCase().includes(query)) ||
                            (feature.v3_note && feature.v3_note.toLowerCase().includes(query)) ||
                            (feature.waba_note && feature.waba_note.toLowerCase().includes(query)) ||
                            (feature.coex_note && feature.coex_note.toLowerCase().includes(query))
                    );

                    return {
                        ...category,
                        features: filteredFeatures,
                    };
                })
                .filter((category) => category.features.length > 0);
        },
    },
};
</script>

<style lang="scss" scoped>
.whatsapp-comparison {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    height: 80vh;

    .comparison-header {
        padding: 24px;
        border-bottom: 1px solid #e8eef3;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-shrink: 0;

        .title {
            margin: 0;
            font-size: 24px;
            font-weight: 600;
            color: #2c3e50;
        }
    }

    .table-wrapper {
        position: relative;
        flex: 1;
        display: flex;
        flex-direction: column;
        border: 1px solid #e8eef3;
        border-radius: 8px;
        overflow: hidden;
        margin: 0 24px;
    }

    .table-header-fixed {
        position: sticky;
        top: 0;
        z-index: 10;
        background: white;
        border-bottom: 2px solid #e8eef3;
    }

    .table-body-scrollable {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
    }

    .comparison-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 14px;

        th,
        td {
            padding: 12px 16px;
            text-align: center;
            border-bottom: 1px solid #f0f0f0;
        }

        th {
            background: #f8f9fa;
            font-weight: 600;
            border-bottom: 2px solid #e8eef3;

            &.feature-column {
                text-align: left;
            }
        }

        .feature-column-width,
        .feature-column {
            width: 300px;
            min-width: 300px;
        }

        .solution-column-width,
        .solution-column {
            width: 200px;
            min-width: 200px;
        }

        .solution-header {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;

            .solution-info {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 4px;

                span:first-child {
                    font-weight: 600;
                    font-size: 14px;
                }

                .solution-subtitle {
                    font-size: 12px;
                    color: #666;
                    font-weight: normal;
                }
            }
        }

        .category-header {
            background: #f5f8fb;

            td {
                padding: 16px;
                border-bottom: 2px solid #dbe1e8;
            }

            .category-title {
                display: flex;
                align-items: center;
                gap: 8px;
                font-weight: 600;
                font-size: 16px;
                color: #2c3e50;

                .material-icons {
                    font-size: 20px;
                    color: #4a6cf7;
                }
            }
        }

        .feature-row {
            &:hover {
                background: #fafbfc;
            }

            .feature-name {
                font-weight: 500;
                color: #2c3e50;
                position: relative;
                text-align: left;

                .info-icon {
                    font-size: 16px;
                    color: #666;
                    margin-left: 8px;
                    cursor: help;
                }
            }

            .support-cell {
                .support-status {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-bottom: 8px;

                    .support-icon {
                        font-size: 28px;
                    }

                    &.supported {
                        .support-icon {
                            color: #52c41a;
                        }
                    }

                    &.partial {
                        .support-icon {
                            color: #fa8c16;
                        }
                    }

                    &.not-supported {
                        .support-icon {
                            color: #ff4d4f;
                        }
                    }
                }

                .support-note {
                    font-size: 12px;
                    color: #666;
                    line-height: 1.4;
                    padding: 6px 8px;
                    background: #f5f5f5;
                    border-radius: 4px;
                    border-left: 3px solid #ddd;
                    text-align: left;
                    margin: 0 8px;
                }
            }
        }
    }

    .comparison-legend {
        position: sticky;
        bottom: 0;
        z-index: 10;
        padding: 16px 24px;
        background: #f8f9fa;
        display: flex;
        gap: 24px;
        border-top: 1px solid #e8eef3;
        margin-top: auto;

        .legend-item {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 14px;

            .material-icons {
                font-size: 20px;

                &.supported {
                    color: #52c41a;
                }

                &.partial {
                    color: #fa8c16;
                }

                &.not-supported {
                    color: #ff4d4f;
                }
            }
        }
    }
    .img {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 12px;
        overflow: hidden;
        position: relative;

        &.webhooks {
            background: #7159c2;
        }

        &.postacell,
        &.ticimax,
        &.holidayplus {
            width: 200px;
        }

        &.ideasoft {
            width: 200px;
        }

        &.supsis {
            background: #e8eef3;
        }

        &.openai {
            background: #a2cbed;
        }

        &.whatsapp {
            background: #58ca64;
        }

        &.whatsapp-coex {
            background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
        }

        &.linkedin {
            background: #e2e5ff;
        }

        &.retell {
            background: #673ab7;

            .material-icons {
                color: #fff;
            }
        }

        &.webphone {
            background: #f5f5f5;

            .material-icons {
                font-size: 40px;
            }
        }

        &.telnyx {
            background: #f0f8ff;

            img {
                object-fit: contain;
            }
        }

        &.netgsm {
            background: #f5f5f5;

            img {
                object-fit: contain;
            }
        }

        &.instagram {
            background: radial-gradient(
                circle at 30% 107%,
                #fdf497 0%,
                #fdf497 5%,
                #fd5949 45%,
                #d6249f 60%,
                #285aeb 90%
            );
        }

        &.telegram {
            background: #29b6f6;
        }

        &.twitter {
            background: #3ecdfb;
        }

        &.slack {
            background: #634360;
        }

        &.mail {
            justify-content: unset !important;

            svg {
                width: 100%;
                height: 100%;
            }
        }

        &.facebook,
        &.skype,
        &.shopify {
            background: #ffffff;
        }

        &.teamgram svg {
            width: 100%;
        }

        &.retell {
            background: #673ab7;
        }

        svg {
            width: 65%;
        }

        &:after {
            background: #fff;
            bottom: 0;
            content: "";
            height: 100%;
            left: -10%;
            opacity: 0.3;
            position: absolute;
            top: 0;
            transform: rotate(45deg) translate(-20%, 150%);
            width: 160%;
        }
    }
}
</style>
